import { makeAutoObservable, runInAction } from 'mobx';

import type { GoStorageObject, IDashboardService, SelectRequestConfig } from '../../../shared/api/IDashboardService';
import { deepClone } from '../../../shared/utils';
import type { TAlertRule } from '../../types';

export type TAlertRuleTemplateData = Omit<
  TAlertRule,
  | 'id'
  | 'createdBy'
  | 'updatedBy'
  | 'createdAt'
  | 'updatedAt'
  | 'minGoStreamCurveId'
  | 'maxGoStreamCurveId'
  | 'notificationTemplateId'
  | 'journalTemplateId'
>;

export type TAlertRulesTemplateData = {
  name: string;
  description: string;
  ownerUserId: string | null;
  data: TAlertRuleTemplateData[];
  indexType: 'time' | 'depth';
};

const ALERT_RULES_TEMPLATE_OBJECT_TYPE = 'GOdashboard_AlertRulesTemplate';

export class AlertRulesTemplatesManager {
  static MAX_TEMPLATE_NAME_LENGTH = 40;

  static MAX_TEMPLATE_DESCRIPTION_LENGTH = 120;

  inProgress = false;

  list: GoStorageObject<TAlertRulesTemplateData>[] = [];

  constructor(
    public readonly indexType: 'time' | 'depth',
    public readonly getOwnerUserId: () => string | null,
    private service: IDashboardService,
  ) {
    makeAutoObservable(this);
  }

  get personalTemplates(): GoStorageObject<TAlertRulesTemplateData>[] {
    return this.list.filter((t) => !!t.data.ownerUserId);
  }

  get publicTemplates(): GoStorageObject<TAlertRulesTemplateData>[] {
    return this.list.filter((t) => !t.data.ownerUserId);
  }

  setTemplates(templates: GoStorageObject<TAlertRulesTemplateData>[]): void {
    this.list = templates;
  }

  setTemplate(template: GoStorageObject<TAlertRulesTemplateData>): void {
    const index = this.list.findIndex((t) => t.id === template.id);
    if (index > -1) {
      this.list.splice(index, 1, template);
    } else {
      this.list.push(template);
    }
  }

  removeTemplate(templateId: number): void {
    const index = this.list.findIndex((t) => t.id === templateId);
    if (index !== -1) {
      this.list.splice(index, 1);
    }
  }

  createTemplate(template: TAlertRulesTemplateData): Promise<GoStorageObject<TAlertRulesTemplateData>> {
    return this.service.createGoStorageObject(ALERT_RULES_TEMPLATE_OBJECT_TYPE, template).then((data) => {
      const object: GoStorageObject<TAlertRulesTemplateData> = {
        id: data.id,
        objectType: data.objectType,
        data: template,
        status: 'ACTIVE',
        createdAt: new Date().getTime() / 1000,
      };
      this.setTemplate(object);
      return object;
    });
  }

  updateTemplateName(templateId: number, newName: string, newDescription?: string) {
    const template = this.list.find((t) => t.id === templateId);
    if (!template) {
      throw new Error('Template not found');
    }
    const temporaryCopy = deepClone(template.data);
    temporaryCopy.name = newName;
    if (newDescription) {
      temporaryCopy.description = newDescription;
    }
    return this.service
      .updateGoStorageObject(ALERT_RULES_TEMPLATE_OBJECT_TYPE, template.id, temporaryCopy)
      .then(() => {
        this.setTemplate({ ...template, data: temporaryCopy });
      })
      .catch((error) => {
        throw error;
      });
  }

  deleteTemplate(templateId: number): Promise<void> {
    return this.service.deleteGoStorageObject(ALERT_RULES_TEMPLATE_OBJECT_TYPE, templateId).then(() => {
      this.removeTemplate(templateId);
    });
  }

  private async duplicateTemplate(
    templateId: number,
    ownerUserId: string | null,
  ): Promise<GoStorageObject<TAlertRulesTemplateData>> {
    const template = this.list?.find((t) => t.id === templateId);

    if (!template) {
      throw new Error('Template not found');
    }

    const temporaryCopy = deepClone(template.data);

    if (ownerUserId) {
      temporaryCopy.ownerUserId = ownerUserId;
    } else {
      temporaryCopy.ownerUserId = null;
    }

    return this.createTemplate(temporaryCopy);
  }

  duplicateTemplateToPersonal(templateId: number): Promise<GoStorageObject<TAlertRulesTemplateData>> {
    const ownerUserId = this.getOwnerUserId();
    if (!ownerUserId) {
      throw new Error('Owner user ID not found');
    }
    return this.duplicateTemplate(templateId, ownerUserId);
  }

  duplicateTemplateToPublic(templateId: number): Promise<GoStorageObject<TAlertRulesTemplateData>> {
    return this.duplicateTemplate(templateId, null);
  }

  async getAll(): Promise<GoStorageObject<TAlertRulesTemplateData>[]> {
    this.inProgress = true;
    const ownerUserId = this.getOwnerUserId();
    if (!ownerUserId) {
      throw new Error('Owner user ID not found');
    }

    const config: SelectRequestConfig = {
      objectType: ALERT_RULES_TEMPLATE_OBJECT_TYPE,
      joinedAlias: ALERT_RULES_TEMPLATE_OBJECT_TYPE,
      where: [
        {
          attr: 'indexType',
          value: this.indexType,
        },
        {
          attr: 'ownerUserId',
          value: ownerUserId,
          additionalConditions: [
            {
              attr: 'ownerUserId',
              value: null,
              operator: 'OR',
            },
          ],
        },
      ],
    };

    return this.service
      .selectData<TAlertRulesTemplateData>(config)
      .then((templates) => {
        runInAction(() => {
          this.list = templates;
        });
        return templates;
      })
      .finally(() => {
        runInAction(() => {
          this.inProgress = false;
        });
      });
  }

  async reloadList(): Promise<void> {
    if (this.inProgress) {
      return;
    }

    this.getAll();
  }

  templateFromList(
    alertRules: TAlertRule[],
    indexType: 'time' | 'depth',
    name: string,
    description: string,
    privateTemplate = true,
  ): TAlertRulesTemplateData {
    const ownerUserId = privateTemplate ? this.getOwnerUserId() : null;

    return {
      name: name,
      description,
      ownerUserId,
      indexType,
      data: alertRules.map((alertRule) => ({
        ...alertRule,
        id: undefined,
        createdBy: undefined,
        updatedBy: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        notificationTemplateId: undefined,
        journalTemplateId: undefined,
        minGoStreamCurveId: undefined,
        maxGoStreamCurveId: undefined,
      })),
    };
  }
}
